import React from 'react';
import {
  ActionDropDown, ActionDropdownOption, ActionDropdownProps, useNotifications,
} from '@doveit/bricks';
import { Design } from '../../../providers/api/dtos';
import { useMarketingDesignExportJobContext } from '../../../contexts/marketing-design-export-job-context/MarketingDesignExportJobContext';
import { MarketingDesignExportFormat, marketingDesignExportFormatLabels, MarketingJobStatus } from '../../../domain/types';
import { createMarketingDesignExportJob } from '../../../providers/api/marketing/marketingProvider';
import useMarketingDesignExportJob from '../../hooks/use-marketing-design-export-job/useMarketingDesignExportJob';
import { downloadFilesFromUrls } from '../../../utils/file/file';

export const MARKETING_DESIGN_EXPORT_JOB_POLL_INTERVAL_MS = 2_000;

interface ChildFnProps {
  isSaving: boolean,
  canAddJob: boolean,
  startExport: (format: MarketingDesignExportFormat) => Promise<void>,
}

export interface ExportMarketingDesignActionProps extends React.AriaAttributes, Pick<ActionDropdownProps, 'disabled' | 'size' | 'emphasis'> {
  design: Design,
  children?: (props: ChildFnProps) => React.ReactNode,
}

const ExportMarketingDesignAction: React.FC<ExportMarketingDesignActionProps> = ({
  design,
  children,
  size = 'S',
  ...rest
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const [pollingJobId, setPollingJobId] = React.useState<string>();

  const { addError } = useNotifications();

  const {
    canAddJob, addJob, updateJob, removeJob,
  } = useMarketingDesignExportJobContext();

  useMarketingDesignExportJob(pollingJobId, {
    refreshInterval: (data) => {
      if (!data) return MARKETING_DESIGN_EXPORT_JOB_POLL_INTERVAL_MS;

      const isTerminal = data.status === MarketingJobStatus.SUCCESS || data.status === MarketingJobStatus.FAILED;
      return isTerminal ? 0 : MARKETING_DESIGN_EXPORT_JOB_POLL_INTERVAL_MS;
    },
    onSuccess: (data) => {
      updateJob(data);

      const isTerminal = data.status === MarketingJobStatus.SUCCESS || data.status === MarketingJobStatus.FAILED;
      if (isTerminal) {
        setPollingJobId(undefined);
        setIsSaving(false);
      }

      if (data.status === MarketingJobStatus.SUCCESS) {
        downloadFilesFromUrls(data.data.urls);
      }
    },
    onError: () => {
      if (pollingJobId) removeJob(pollingJobId);
      setPollingJobId(undefined);
      setIsSaving(false);
    },
  });

  const handleStartExport = React.useCallback(async (format: MarketingDesignExportFormat) => {
    if (!canAddJob) return;

    try {
      setIsSaving(true);

      const startedExportJob = await createMarketingDesignExportJob({
        format,
        designId: design.id,
      });

      const { job, error } = addJob(startedExportJob, design);
      if (!error) {
        setPollingJobId(job.id);
      } else {
        setIsSaving(false);
      }
    } catch (err) {
      addError('Non è stato possibile scaricare il design.');
      setIsSaving(false);
    }
  }, [addError, addJob, canAddJob, design]);

  const options: ActionDropdownOption[] = React.useMemo(() => Object.values(MarketingDesignExportFormat).map((format) => ({
    key: format,
    label: marketingDesignExportFormatLabels[format],
    onClick: () => handleStartExport(format),
  })), [handleStartExport]);

  return children?.({ isSaving, canAddJob, startExport: handleStartExport }) || (
    <ActionDropDown
      aria-label="Selezione formato di esportazione"
      label="Scarica design"
      options={options}
      size={size}
      disabled={!canAddJob}
      loading={isSaving}
      emphasis="low"
      width="100%"
      {...rest}
    />
  );
};

export default ExportMarketingDesignAction;
